import type { BlockDto, BlockLinkDto, ScenarioDto } from '@/api-gen/api.schemas';
import { BlockType, VoiceResponseUsecase } from '@/api-gen/api.schemas';
import type { CustomNode, CustomNodeType } from '../../../models/intents/customNode.ts';
import { getNodePosition, getChartOffset } from '../positionHelpers.ts';
import { getDefaultNodeSize } from '../sizingHelpers.ts';
import getDefaultPorts, { getOutputPortId, getInputPortId } from '../portsHelpers.ts';
import type { ILink } from '@mrblenny/react-flow-chart';
import type { CustomChart } from '../../../models/scenarios/customChart.ts';
import type ChartDOMPosition from '../../../models/guiState/chartDomPosition.ts';
import type { LinkedScenario } from '../../../models/intents/intentNodeProperties.ts';
import { MapEnumToColor } from '../../colorEnumToStringMapper.ts';

import { CustomNodeTypeEnum } from 'app/models/intents/customNode.ts';

export function nodeFromBlock(block: BlockDto, linkedScenarios: Array<LinkedScenario>): CustomNode {
  return {
    id: `${block.id}`,
    type: nodeTypeFromBlockType(block.type),
    position: getNodePosition({
      x: Math.round(block?.posX ?? 0),
      y: Math.round(block?.posY ?? 0)
    }),

    size: getDefaultNodeSize(),
    ports: getDefaultPorts(nodeTypeFromBlockType(block.type) == CustomNodeTypeEnum.Start),
    properties: {
      scenarioPersonaId: block.scenarioPersonaId,
      name: block.name,
      description: block.description,
      intentPhrases: block.intentPhrases ?? [],
      aiResponses:
        block.aiResponses?.filter((resp) => resp.voiceResponseUsecase === VoiceResponseUsecase.Success) ?? [],
      fallbackResponses:
        block.aiResponses?.filter((resp) => resp.voiceResponseUsecase === VoiceResponseUsecase.Fail) ?? [],
      labelColor: MapEnumToColor(block.qualityLevel),
      linkedScenarios: linkedScenarios,
      globalAssetIntents: block.globalAssetIntents
    }
  };
}

export function linkFromSceneLink(sceneLink: BlockLinkDto): ILink {
  const link: ILink = {
    id: `${sceneLink.id}`,
    from: {
      nodeId: `${sceneLink.originId}`,
      portId: getOutputPortId()
    },
    to: {
      nodeId: `${sceneLink.targetId}`,
      portId: getInputPortId()
    }
  };

  return link;
}

export function chartFromSceneFull(
  scene: ScenarioDto,
  chartDOMInfo: ChartDOMPosition,
  scenarios: Array<ScenarioDto>
): CustomChart {
  const nodes =
    scene.blocks?.reduce<Record<string, CustomNode>>((nodesObject, block) => {
      const externalLinks: Array<LinkedScenario> = scene.blockLinks
        ?.filter((x) => x.origin?.sceneId != x.target?.sceneId && x.originId == block.id)
        .map((x) => {
          const linkedScenario: LinkedScenario = {
            sceneId: x.target?.sceneId,
            sceneName: scenarios.find((s) => s.id === x.target?.sceneId)?.name,
            id: x.id,
            startBlockId: scenarios.find((s) => s.id === x.target?.sceneId)?.startBlockId
          };
          return linkedScenario;
        });

      nodesObject[block.id] = nodeFromBlock(block, externalLinks);
      return nodesObject;
    }, {}) ?? {};

  const links =
    scene.blockLinks
      ?.filter((x) => x.origin?.sceneId == x.target?.sceneId) // or use x.sceneId ??
      .reduce<Record<string, ILink>>((linksObject, sceneLink) => {
        linksObject[sceneLink.id] = linkFromSceneLink(sceneLink);
        return linksObject;
      }, {}) ?? {};

  const chartScale = getValidScale(scene.scale);

  const chart: CustomChart = {
    scale: chartScale,
    offset: getChartOffset(
      {
        x: scene.centerX,
        y: scene.centerY
      },
      {
        x: chartDOMInfo.chartDOMWidth,
        y: chartDOMInfo.chartDOMHeight
      },
      chartScale
    ),
    nodes: nodes,
    links: links,
    selected: {},
    hovered: {},
    properties: {
      id: scene.id,
      hoveredPort: null,
      showIntentEdit: false
    }
  };

  return chart;
}

/// scale info
// these variables are extracted from react-flow-chart source code and are subject to change
const minScale = 0.25;
const maxScale = 2;

function getValidScale(sceneScale?: number): number {
  return Math.min(Math.max(sceneScale ?? 1, minScale), maxScale);
}

/// enum type maps

const blockTypeNodeTypeMap: Record<BlockType, CustomNodeType> = {
  [BlockType.Normal]: CustomNodeTypeEnum.Normal,
  [BlockType.Start]: CustomNodeTypeEnum.Start,
  [BlockType.End]: CustomNodeTypeEnum.End,
  [BlockType.Service]: CustomNodeTypeEnum.Service
};

function nodeTypeFromBlockType(bt: BlockType): CustomNodeType {
  return blockTypeNodeTypeMap[bt];
}
